import { FallbackError } from "@core/domain/errors/fallback.error";
import type { ModelError } from "@iot-platform/core";
import type { LatestDataDto } from "@station/data/dto/latest-data.dto";
import { LatestDataDtoSchema } from "@station/data/dto/latest-data.dto";
import type { StationDto } from "@station/data/dto/station.dto.ts";
import { StationDtoSchema } from "@station/data/dto/station.dto.ts";
import { LatestData } from "@station/domain/models/latest-data.model";
import type { StationUpdate } from "@station/domain/models/station.model.ts";
import {
    Station,
    StationUpdateSchema,
} from "@station/domain/models/station.model.ts";
import { injectable } from "inversify";
import { err, ok, Result } from "neverthrow";
// eslint-disable-next-line boundaries/dependencies
import type { StationAdminFormValuesValidated } from "@admin/presentation/components/station-admin-form.view";
import type * as z from "zod";

const NUM_TWO = 2;

@injectable()
export class StationMapper {
    mapLatestData(
        latestDataDto: LatestDataDto,
    ): Result<LatestData[], ModelError<typeof LatestData> | FallbackError> {
        const latestDataDtoParsed =
            LatestDataDtoSchema.safeParse(latestDataDto);

        if (!latestDataDtoParsed.success) {
            console.error(latestDataDtoParsed.error.issues);
            return err(new FallbackError());
        }

        const station = Object.values(latestDataDtoParsed.data)[0];
        if (!station) return err(new FallbackError());

        const latestDataResults = Object.entries(station).map(
            ([sensorId, sensorData]) =>
                LatestData.createWithRecovery({
                    id: parseInt(sensorId),
                    value: sensorData.value,
                    timeStamp: sensorData.timestamp, //Convert to date
                    tendency: sensorData.tendency ?? null,
                }).orElse((error) => {
                    if (error.recuperable) {
                        return ok(error.instance);
                    }

                    return err(error);
                }),
        );

        const latestDataResult = Result.combine(latestDataResults);

        return latestDataResult;
    }

    map(
        stationDto: StationDto,
    ): Result<Station, ModelError<typeof Station> | FallbackError> {
        const stationDtoParsed = StationDtoSchema.safeParse(stationDto);

        if (!stationDtoParsed.success) {
            console.error(
                "StationDtoSchema parsing failed:",
                stationDtoParsed.error.issues,
            );
            return err(new FallbackError());
        }

        return Station.createWithRecovery({
            id: stationDto.id,
            code: stationDto.code,
            name: stationDto.name,
            technicalName: stationDto.technical_name ?? "",
            latitude: stationDto.latitude ?? "",
            longitude: stationDto.longitude ?? "",
            radius: stationDto.radius ?? 0,
            type: stationDto.type,
            oldThresholdHours: stationDto.old_threshold_hours,
            clientCompany: stationDto.client_company ?? "",
            icon: stationDto.icon,
            lastFrameAt: stationDto.last_frame_at ?? "",
            lastFrameTimestamp: stationDto.last_frame_timestamp ?? "",
            vehicleType: stationDto.vehicle_type ?? "",
            allowDrainageReport: stationDto.allow_drainage_report,
            decisionSupportSystems:
                stationDto.decision_support_systems?.map((dss) => ({
                    id: dss.id,
                    configuration: {
                        id: dss.configuration.id,
                        type: dss.configuration.type,
                        name: dss.configuration.name,
                        description: dss.configuration.description ?? "",
                    },
                })) ?? [],
            pois:
                stationDto.pois?.map((poi) => ({
                    id: poi.id,
                    code: poi.code,
                    name: poi.name,
                    technicalName: poi.technical_name,
                    type: poi.type,
                })) ?? [],
            batteryLevel: stationDto.battery_percentage
                ? Number(stationDto.battery_percentage.toFixed(NUM_TWO))
                : null,
            campaign: stationDto.campaign_active
                ? {
                      id: stationDto.campaign_active.id,
                      name: stationDto.campaign_active.name,
                      cropType: {
                          id: stationDto.campaign_active.crop_type.id,
                          name: stationDto.campaign_active.crop_type.code,
                      },
                  }
                : null,
        });
    }

    mapFormToEdit(
        id: number,
        station: StationAdminFormValuesValidated,
    ): Result<StationUpdate, z.ZodError<StationUpdate>> {
        const maybeStationUpdate = StationUpdateSchema.safeParse({
            id,
            name: station.name,
            technicalName: station.technicalName ?? null,
            latitude: station.latitude ?? null,
            longitude: station.longitude ?? null,
        } satisfies StationUpdate);

        return maybeStationUpdate.success
            ? ok(maybeStationUpdate.data)
            : err(maybeStationUpdate.error);
    }
}
